import { useSearchParams } from 'react-router-dom';

import { BudgetStatus, MatchingSource, RecommendationFilters } from './matching.types';
import { recommendationParams } from './useRecommendations';

const integer = (value: string | null, fallback: number, min: number, max: number) =>
  value !== null && /^\d+$/.test(value) && Number(value) >= min && Number(value) <= max ? Number(value) : fallback;
const budgetValues: BudgetStatus[] = ['within_budget', 'over_budget', 'unknown'];

export const useRecommendationSearch = (source: MatchingSource) => {
  const [search, setSearch] = useSearchParams();
  const max = integer(search.get('max'), 20, 1, 100);
  const offset = integer(search.get('from'), 0, 0, Number.MAX_SAFE_INTEGER);
  const from = Math.floor(offset / max) * max;
  const minScore = integer(search.get('minScore'), -1, 0, 100);
  const freshness = search.get('freshness');
  const active = search.get('active');
  const filters: RecommendationFilters = {
    ...(minScore >= 0 ? { minScore } : {}),
    budgetStatus: budgetValues.filter((value) => search.getAll('budgetStatus').includes(value)),
    ...(freshness === 'current' || freshness === 'stale' ? { freshness } : {}),
    ...(source.type === 'candidate' && (active === 'true' || active === 'false') ? { active: active === 'true' } : {}),
    ...(search.get('excludeExistingRecruitment') === 'true' ? { excludeExistingRecruitment: true } : {}),
    excludeRecruitmentStatusIds: Array.from(
      new Set(
        search
          .getAll('excludeRecruitmentStatusIds')
          .map((value) => integer(value, -1, 1, Number.MAX_SAFE_INTEGER))
          .filter((value) => value > 0)
      )
    ).slice(0, 100),
  };
  const advanced = search.get('advanced') === '1';
  const write = (next: RecommendationFilters, nextFrom = 0, nextMax = max, expanded = advanced) => {
    const params = new URLSearchParams(recommendationParams(source, next, nextFrom, nextMax));
    if (expanded) params.set('advanced', '1');
    setSearch(params, { replace: true });
  };
  return {
    filters,
    from,
    max,
    advanced,
    filtered: !!(
      filters.minScore !== undefined ||
      filters.budgetStatus?.length ||
      filters.active !== undefined ||
      filters.freshness ||
      filters.excludeExistingRecruitment ||
      filters.excludeRecruitmentStatusIds?.length
    ),
    setFilters: (patch: Partial<RecommendationFilters>) => write({ ...filters, ...patch }),
    clearFilters: () => write({}),
    setPage: (offset: number, size = max) => write(filters, size === max ? offset : 0, size),
    setAdvanced: (expanded: boolean) => write(filters, from, max, expanded),
    search: search.toString(),
  };
};
export type RecommendationSearch = ReturnType<typeof useRecommendationSearch>;
